import React, { useEffect, useState } from 'react';
import { collection, getDocs, writeBatch, doc } from 'firebase/firestore';
import { db } from '../services/firebase';
import styles from '../styles/LowStockDashboard.module.css';

const URGENCY_LEVELS = {
  CRITICAL: { label: 'Critical', color: '#dc3545', priority: 1 }, // Out of stock or <25% of par
  HIGH: { label: 'High', color: '#fd7e14', priority: 2 },       // 25-50% of par
  MEDIUM: { label: 'Medium', color: '#ffc107', priority: 3 },   // 50-75% of par
  LOW: { label: 'Low', color: '#28a745', priority: 4 }          // 75-100% of par (but below par)
};

function getUrgencyLevel(item) {
  if (!item.par_level || item.par_level <= 0) return URGENCY_LEVELS.MEDIUM;
  
  const ratio = item.on_hand / item.par_level;
  
  if (item.on_hand <= 0 || ratio < 0.25) return URGENCY_LEVELS.CRITICAL;
  if (ratio < 0.5) return URGENCY_LEVELS.HIGH;
  if (ratio < 0.75) return URGENCY_LEVELS.MEDIUM;
  return URGENCY_LEVELS.LOW;
}

function calculateReorderQuantity(item) {
  // Suggest reordering to 120% of par level for buffer
  const targetStock = Math.ceil(item.par_level * 1.2);
  return Math.max(targetStock - item.on_hand, 0);
}

function LowStockCard({ item, onQuickReorder, onEditStock }) {
  const urgency = getUrgencyLevel(item);
  const reorderQty = calculateReorderQuantity(item);
  const [isReordering, setIsReordering] = useState(false);

  const handleQuickReorder = async () => {
    setIsReordering(true);
    await onQuickReorder(item, reorderQty);
    setIsReordering(false);
  };

  return (
    <div className={`${styles.lowStockCard} ${styles[urgency.label.toLowerCase()]}`}>
      <div className={styles.cardHeader}>
        <div className={styles.itemInfo}>
          <h3 className={styles.itemName}>{item.name}</h3>
          <span className={styles.itemBranch}>{item.branch}</span>
        </div>
        <div className={styles.urgencyBadge} style={{ backgroundColor: urgency.color }}>
          {urgency.label}
        </div>
      </div>
      
      <div className={styles.stockInfo}>
        <div className={styles.stockMetrics}>
          <div className={styles.metric}>
            <span className={styles.metricLabel}>Current Stock</span>
            <span 
              className={styles.metricValue}
              onClick={() => onEditStock(item)}
              title="Click to edit"
            >
              {item.on_hand || 0} {item.unit}
            </span>
          </div>
          <div className={styles.metric}>
            <span className={styles.metricLabel}>Par Level</span>
            <span className={styles.metricValue}>{item.par_level || 0} {item.unit}</span>
          </div>
          <div className={styles.metric}>
            <span className={styles.metricLabel}>Shortage</span>
            <span className={styles.metricValue}>
              {Math.max(item.par_level - item.on_hand, 0)} {item.unit}
            </span>
          </div>
        </div>
        
        <div className={styles.stockProgress}>
          <div className={styles.progressBar}>
            <div 
              className={styles.progressFill}
              style={{ 
                width: `${Math.min((item.on_hand / item.par_level) * 100, 100)}%`,
                backgroundColor: urgency.color 
              }}
            />
          </div>
          <span className={styles.progressText}>
            {Math.round((item.on_hand / item.par_level) * 100)}% of par level
          </span>
        </div>
      </div>

      <div className={styles.cardActions}>
        <button 
          onClick={handleQuickReorder}
          disabled={isReordering}
          className={styles.reorderButton}
        >
          {isReordering ? 'Reordering...' : `Reorder ${reorderQty} ${item.unit}`}
        </button>
        <button className={styles.detailsButton}>
          View Details
        </button>
      </div>
    </div>
  );
}

export default function LowStockDashboard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedUrgency, setSelectedUrgency] = useState('');
  const [editingStock, setEditingStock] = useState(null);
  const [tempStockValue, setTempStockValue] = useState('');

  useEffect(() => {
    async function fetchLowStockItems() {
      setLoading(true);
      setError(null);
      try {
        const snap = await getDocs(collection(db, 'inventoryItems'));
        const allItems = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Filter to only show items below par level
        const lowStockItems = allItems.filter(item => {
          const parLevel = item.par_level || 0;
          const onHand = item.on_hand || 0;
          return parLevel > 0 && onHand < parLevel;
        });

        setItems(lowStockItems);
      } catch (err) {
        setError('Failed to load low stock items.');
        console.error(err);
      }
      setLoading(false);
    }
    fetchLowStockItems();
  }, []);

  const filteredItems = items.filter(item => {
    if (selectedBranch && item.branch !== selectedBranch) return false;
    if (selectedUrgency) {
      const urgency = getUrgencyLevel(item);
      if (urgency.label.toLowerCase() !== selectedUrgency) return false;
    }
    return true;
  });

  // Sort by urgency priority, then by shortage percentage
  const sortedItems = filteredItems.sort((a, b) => {
    const urgencyA = getUrgencyLevel(a);
    const urgencyB = getUrgencyLevel(b);
    
    if (urgencyA.priority !== urgencyB.priority) {
      return urgencyA.priority - urgencyB.priority; // Lower priority number = higher urgency
    }
    
    // If same urgency, sort by shortage percentage (lowest stock ratio first)
    const ratioA = a.on_hand / a.par_level;
    const ratioB = b.on_hand / b.par_level;
    return ratioA - ratioB;
  });

  const handleQuickReorder = async (item, quantity) => {
    try {
      const batch = writeBatch(db);
      const itemRef = doc(db, 'inventoryItems', item.id);
      const newStock = item.on_hand + quantity;
      batch.update(itemRef, { on_hand: newStock });
      await batch.commit();

      // Update local state
      setItems(prev => 
        prev.map(i => 
          i.id === item.id 
            ? { ...i, on_hand: newStock }
            : i
        )
      );

      // Log the reorder action
      console.log(`Reordered ${quantity} ${item.unit} of ${item.name}`);
    } catch (err) {
      console.error('Failed to reorder item:', err);
      alert('Failed to reorder item. Please try again.');
    }
  };

  const handleEditStock = (item) => {
    setEditingStock(item.id);
    setTempStockValue(item.on_hand || 0);
  };

  const saveStockEdit = async (itemId) => {
    try {
      const newValue = Number(tempStockValue);
      if (newValue < 0) {
        alert('Stock value cannot be negative.');
        return;
      }

      const batch = writeBatch(db);
      const itemRef = doc(db, 'inventoryItems', itemId);
      batch.update(itemRef, { on_hand: newValue });
      await batch.commit();

      setItems(prev => 
        prev.map(item => 
          item.id === itemId 
            ? { ...item, on_hand: newValue }
            : item
        )
      );

      setEditingStock(null);
      setTempStockValue('');
    } catch (err) {
      alert('Failed to update stock. Please try again.');
      console.error(err);
    }
  };

  const branches = [...new Set(items.map(item => item.branch).filter(Boolean))];
  const urgencyLevels = Object.values(URGENCY_LEVELS);

  const urgencyCounts = urgencyLevels.reduce((acc, level) => {
    acc[level.label.toLowerCase()] = filteredItems.filter(item => 
      getUrgencyLevel(item).label === level.label
    ).length;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading low stock items...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  return (
    <div className={styles.dashboardContainer}>
      <div className={styles.header}>
        <h1 className={styles.pageTitle}>Low Stock Alert Dashboard</h1>
        <div className={styles.summaryCards}>
          {urgencyLevels.map(level => (
            <div 
              key={level.label}
              className={styles.summaryCard}
              style={{ borderColor: level.color }}
            >
              <div className={styles.summaryCount}>{urgencyCounts[level.label.toLowerCase()]}</div>
              <div className={styles.summaryLabel}>{level.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.controls}>
        <div className={styles.filters}>
          <select 
            value={selectedBranch} 
            onChange={e => setSelectedBranch(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="">All Branches</option>
            {branches.map(branch => (
              <option key={branch} value={branch}>{branch}</option>
            ))}
          </select>
          
          <select 
            value={selectedUrgency} 
            onChange={e => setSelectedUrgency(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="">All Urgency Levels</option>
            {urgencyLevels.map(level => (
              <option key={level.label} value={level.label.toLowerCase()}>
                {level.label} Priority
              </option>
            ))}
          </select>
        </div>
        
        <div className={styles.actionButtons}>
          <button className={styles.refreshButton} onClick={() => window.location.reload()}>
            Refresh
          </button>
          <button className={styles.exportButton}>
            Export Report
          </button>
        </div>
      </div>

      <div className={styles.itemsGrid}>
        {sortedItems.length === 0 ? (
          <div className={styles.noItemsMessage}>
            <h3>🎉 Great job!</h3>
            <p>No items are currently below par level.</p>
          </div>
        ) : (
          sortedItems.map(item => (
            <LowStockCard
              key={item.id}
              item={item}
              onQuickReorder={handleQuickReorder}
              onEditStock={handleEditStock}
            />
          ))
        )}
      </div>

      {/* Quick Edit Modal */}
      {editingStock && (
        <div className={styles.editModal}>
          <div className={styles.editModalContent}>
            <h3>Edit Stock Quantity</h3>
            <input
              type="number"
              min="0"
              value={tempStockValue}
              onChange={e => setTempStockValue(e.target.value)}
              className={styles.editInput}
              autoFocus
            />
            <div className={styles.editActions}>
              <button onClick={() => saveStockEdit(editingStock)} className={styles.saveButton}>
                Save
              </button>
              <button onClick={() => setEditingStock(null)} className={styles.cancelButton}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 